import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import CheckCircleLight from '@icon/CheckCircleLight';
import LockOutline from '@icon/LockOutline';
import useOngoingSection from '@cont/Quiz/Taking/Common/useOngoingSection';
import { NTA_COLORS } from '../constants';
import { ntaButtonCss } from '../typography';

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ $hasIcon }) => ($hasIcon ? '8px' : '0')};
  border: 1px solid
    ${({ $active }) => ($active ? NTA_COLORS.textPrimary : NTA_COLORS.divider)};
  border-radius: 4px;
  padding: 10px 20px;
  padding-left: ${({ $hasIcon }) => ($hasIcon ? '14px' : '20px')};
  background: ${NTA_COLORS.white};
  color: ${({ $completed }) =>
    $completed ? NTA_COLORS.textSecondary : NTA_COLORS.textPrimary};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  ${ntaButtonCss}
  font-weight: ${({ $active }) => ($active ? 700 : 400)};
  text-transform: uppercase;
`;

const IconWrap = styled.span`
  display: inline-flex;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
`;

const NtaSectionChip = ({ section, index, isActive, onSectionClick }) => {
  const { isLocked, isCompleted, hasOngoingSectional } = useOngoingSection(
    section.uid
  );
  const showCompleted = hasOngoingSectional && isCompleted;
  const showLocked = hasOngoingSectional && isLocked && !isCompleted;
  const isDisabled = showCompleted || showLocked;
  const label = section.title || `SECTION ${index + 1}`;

  const handleClick = () => {
    if (isDisabled) return;
    onSectionClick(section.uid);
  };

  return (
    <Chip
      type="button"
      $active={isActive}
      $completed={showCompleted}
      $hasIcon={showCompleted || showLocked}
      $disabled={isDisabled}
      disabled={isDisabled}
      onClick={handleClick}
      aria-disabled={isDisabled}
    >
      {showCompleted ? (
        <IconWrap aria-hidden>
          <CheckCircleLight size={16} color={NTA_COLORS.textSecondary} />
        </IconWrap>
      ) : null}
      {showLocked ? (
        <IconWrap aria-hidden>
          <LockOutline width={14} height={16} color={NTA_COLORS.textPrimary} />
        </IconWrap>
      ) : null}
      {label}
    </Chip>
  );
};

NtaSectionChip.propTypes = {
  section: PropTypes.shape({
    uid: PropTypes.string.isRequired,
    title: PropTypes.string
  }).isRequired,
  index: PropTypes.number.isRequired,
  isActive: PropTypes.bool.isRequired,
  onSectionClick: PropTypes.func.isRequired
};

export default NtaSectionChip;
